import type { MatterStatus } from "@/lib/types";

export const MATTER_STATUS_CATEGORY_OPTIONS = [
  "lead",
  "opportunity",
  "dismissed",
  "pre-litigation",
  "litigation",
  "closed",
  "referred",
  "declined",
] as const;

export type MatterStatusCategoryOption =
  (typeof MATTER_STATUS_CATEGORY_OPTIONS)[number];

export const INTAKE_STATUS_CATEGORY_OPTIONS = [
  "lead",
  "opportunity",
  "dismissed",
] as const satisfies readonly MatterStatusCategoryOption[];

export const LEAD_STATUS_CATEGORY_OPTIONS = [
  "lead",
] as const satisfies readonly MatterStatusCategoryOption[];

export const OPPORTUNITY_STATUS_CATEGORY_OPTIONS = [
  "opportunity",
] as const satisfies readonly MatterStatusCategoryOption[];

export const DISMISSED_STATUS_CATEGORY_OPTIONS = [
  "dismissed",
] as const satisfies readonly MatterStatusCategoryOption[];

export const CASE_MATTER_STATUS_CATEGORY_OPTIONS = [
  "pre-litigation",
  "litigation",
  "closed",
  "declined",
] as const satisfies readonly MatterStatusCategoryOption[];

export const ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS = [
  "pre-litigation",
  "litigation",
] as const satisfies readonly MatterStatusCategoryOption[];

export const REFERRAL_STATUS_CATEGORY_OPTIONS = [
  "referred",
] as const satisfies readonly MatterStatusCategoryOption[];

export const getMatterStatusCategoryScope = (
  categoryId: string | null | undefined,
): readonly MatterStatusCategoryOption[] => {
  if (categoryId === "lead") {
    return LEAD_STATUS_CATEGORY_OPTIONS;
  }

  if (categoryId === "opportunity") {
    return OPPORTUNITY_STATUS_CATEGORY_OPTIONS;
  }

  if (categoryId === "dismissed") {
    return DISMISSED_STATUS_CATEGORY_OPTIONS;
  }

  if (
    CASE_MATTER_STATUS_CATEGORY_OPTIONS.includes(
      categoryId as (typeof CASE_MATTER_STATUS_CATEGORY_OPTIONS)[number],
    )
  ) {
    return CASE_MATTER_STATUS_CATEGORY_OPTIONS;
  }

  if (categoryId === "referred") {
    return REFERRAL_STATUS_CATEGORY_OPTIONS;
  }

  return [];
};

export type MatterStatusTransitionAction = "referred" | "closed" | "reopen";

export const getMatterStatusTransitionActions = (
  categoryId: string | null | undefined,
): { action: MatterStatusTransitionAction; label: string }[] => {
  if (categoryId === "closed") {
    return [{ action: "reopen", label: "Reopen case…" }];
  }

  if (categoryId === "referred") {
    return [
      { action: "reopen", label: "Reopen case…" },
      { action: "closed", label: "Close case…" },
    ];
  }

  return [
    { action: "referred", label: "Refer case out…" },
    { action: "closed", label: "Close case…" },
  ];
};

export const MATTER_STATUS_CATEGORY_DISPLAY_LABELS: Record<
  MatterStatusCategoryOption,
  string
> = {
  lead: "Lead",
  opportunity: "Opportunity",
  dismissed: "Dismissed",
  "pre-litigation": "Pre-Litigation",
  litigation: "Litigation",
  closed: "Closed",
  referred: "Referred",
  declined: "Declined",
};

export const isMatterStatusCategoryOption = (
  value: string | null | undefined,
): value is MatterStatusCategoryOption =>
  MATTER_STATUS_CATEGORY_OPTIONS.includes(value as MatterStatusCategoryOption);

export const getMatterStatusCategoryLabel = (
  categoryId: string | null | undefined,
) =>
  isMatterStatusCategoryOption(categoryId)
    ? MATTER_STATUS_CATEGORY_DISPLAY_LABELS[categoryId]
    : (categoryId ?? "");

export type MatterStatusGroup = {
  category: string;
  items: { value: string; label: string }[];
};

export const buildMatterStatusGroups = (
  statuses: MatterStatus[],
): MatterStatusGroup[] =>
  statuses.reduce<MatterStatusGroup[]>((groups, status) => {
    const existingGroup = groups.find(
      (group) => group.category === status.categoryId,
    );
    const item = { label: status.label, value: status.id };

    if (existingGroup) {
      existingGroup.items.push(item);
    } else {
      groups.push({ category: status.categoryId, items: [item] });
    }

    return groups;
  }, []);

export const MATTER_CLIENT_MARITAL_STATUS_OPTIONS = [
  "Single",
  "Married",
  "Divorced",
  "Separated",
  "Widowed",
] as const;

export const MATTER_CONTACT_CONNECTION_LABEL_OPTIONS = [
  "Accessor",
  "Adjuster Supervisor",
  "Bodily Injury Adjuster",
  "Auto Shop",
  "Client",
  "Spouse",
  "Emergency Contact",
  "Defendant",
  "Statute Defendant",
  "Driver",
  "Insurance Provider",
  "Insurance Representative",
  "Insured",
  "Insurer",
  "Lien Holder",
  "Lien Representative",
  "MedPay Adjuster",
  "Expert",
  "Third Party",
  "Other",
  "Passenger",
  "Payee",
  "Pedestrian",
  "Policy Holder",
  "Property Damage Adjuster",
  "Police Department",
  "Primary Contact",
  "Referral",
  "Referee",
  "Parent",
  "Provider",
  "Fulfiller",
  "Treating Physician",
  "Registered Owner",
  "Signer1",
  "Subrogation Representative",
  "Subrogation Vendor",
  "Witness",
  ...Array.from({ length: 99 }, (_, i) => `Signer${i + 1}`), // allow Signer1 - Signer100
] as const;

export const MATTER_CONTACT_CONNECTION_WITNESS_STATUS_OPTIONS = [
  "Pending Contact",
  "Unresponsive",
  "Uncooperative",
  "Pending Statement",
  "Received Statement",
  "NA",
] as const;

export const MATTER_VEHICLE_DAMAGE_SEVERITY_OPTIONS = [
  "Unknown",
  "None",
  "Minor",
  "Moderate",
  "Major",
  "Rollover",
] as const;

export const MATTER_TASK_STATUS_OPTIONS = [
  "Open",
  "In Progress",
  "Completed",
] as const;

export const MATTER_INCIDENT_REPORT_STATUS_OPTIONS = [
  "Not Requested",
  "Requested",
  "Received",
] as const;

export const MEDICAL_TREATMENT_STATUS_OPTIONS = [
  "Scheduled",
  "In Progress",
  "Completed",
  "Cancelled",
] as const;

export const MEDICAL_TREATMENT_RECORD_STATUS_OPTIONS = [
  "Not Requested",
  "Requested",
  "Received",
  "Rejected",
  "N/A",
] as const;

export const MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS = [
  "Requested",
  "Received",
  "Rejected",
] as const;

export const MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS = [
  "Signed LOP",
] as const;

export const MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS = [
  "Lien Filed",
  "Lien Signed",
] as const;

export const MEDICAL_TREATMENT_BILLING_STATUS_OPTIONS = [
  "Requested",
  "Received",
  "N/A",
] as const;

export const MATTER_LIEN_STATUS_OPTIONS = [
  "Unpaid",
  "Partially Paid",
  "Paid",
] as const;

export const MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS = [
  { value: "25", label: "25" },
  { value: "30", label: "30" },
  { value: "33.33", label: "33 1/3" },
  { value: "40", label: "40" },
  { value: "50", label: "50" },
];

export const MEDICAL_TREATMENT_MISSED_REASONS = [
  "No Show",
  "Forgot",
  "Reschedule",
  "Reluctance to treatment",
] as const;

export const MATTER_TEMPLATE_CATEGORY_OPTIONS = [
  "Expense",
  "Incident Report",
  "Insurance",
  "Lien",
  "Lien Reduction Request",
  "Lost Wage",
  "Medical Treatment Appointment",
  "Medical Treatment Bill",
  "Medical Treatment Plan",
  "Medical Treatment Record",
  "Medical Treatment",
  "Property Damage",
  "Settlement Negotiation",
  "Signature Request",
  "Trust Check",
  "Vehicle",
] as const;
